import { recoverInvalidSession } from './session-recovery';
import { applyPhoneRateLimit } from '@/composables/useResendTimer';
import { getUnavailableAccess } from './access-unavailable';
// NPM
import axios from 'axios';

// Interfaces
import type { AxiosRequestHeaders, AxiosError } from 'axios';
import {
  findError,
  type ErrorKeysType,
} from '@magma-app/magma-errors/dist/index';
import { getDeviceId } from '@/utils/device-id';

// Stores
import { useCommonStore } from '@/stores/common';

// i18n (locale follows the user's language, set in App.vue)
import i18n from '@/plugins/i18n';

// Picks the error message in the user's language, falling back to English
export const localizeErrorMessage = (message?: { fr: string; en: string }) => {
  const lang = i18n.global.locale.value as keyof NonNullable<typeof message>;
  return message?.[lang] || message?.en;
};

// Auth token helpers
import { clearJwtTokens, readJwtToken } from '@/services/auth-tokens';
import { saveReturnTo } from '@/services/return-to';

// Centralized Axios instances
export const instances = {
  main: axios.create({
    baseURL: import.meta.env.VITE_MAGMA_API,
  }),
  prospectDashboard: axios.create({
    baseURL: import.meta.env.VITE_MAGMA_API + 'prospect-dashboard/',
  }),
  userDashboard: axios.create({
    baseURL: import.meta.env.VITE_MAGMA_API_USER_DASHBOARD,
    timeout: 30000, // 30 second timeout to prevent infinite hangs
  }),
  reviews: axios.create({
    baseURL: import.meta.env.VITE_MAGMA_API,
  }),
  helperSignUp: axios.create({
    baseURL: import.meta.env.VITE_MAGMA_API,
  }),
  base: axios.create({
    baseURL: import.meta.env.VITE_MAGMA_API,
  }),
};

// Helpers
const clearAuth = () => {
  clearJwtTokens();
  try {
    localStorage.removeItem('auth');
  } catch {
    // Do nothing
  }
};

const normalizeRedirect = (dest?: string) => {
  if (!dest || dest === '/') return '/login';
  return dest;
};

const hardRedirect = (dest?: string) => {
  clearAuth();
  const target = normalizeRedirect(dest);
  if (window.location.pathname !== target) {
    window.location.replace(target);
  }
};

const redirectUnavailableAccess = (error: AxiosError) => {
  const unavailable = getUnavailableAccess(error);
  if (!unavailable) return false;
  // A closed conversation does not revoke access to the rest of an active account.
  if (unavailable.reason === 'account') clearAuth();
  if (window.location.pathname !== '/no-access') {
    const query = new URLSearchParams({ reason: unavailable.reason });
    if (unavailable.supportReference) query.set('ref', unavailable.supportReference);
    window.location.replace(`/no-access?${query}`);
  }
  return true;
};

type ProviderPhoneError = {
  status?: string;
  reason?: string;
};

type MappedErrorType = {
  type: string;
  subtype: string;
};

const _isPhoneFlowRoute = (url: string): boolean => {
  return (
    url.includes('/sign-up-prospect/phone/')
      || url.includes('/sign-up-prospect/main-information')
    || url.includes('/sign-up-prospect/referred/phone/')
    || url.includes('/sign-up-prospect/referred')
    || url.includes('/sign-up-ambassador/phone')
  );
};

const parseProviderPhoneError = (data: any): ProviderPhoneError | null => {
  if (!data) return null;

  const responsePayload = data?.response;
  if (!responsePayload) return null;

  if (typeof responsePayload === 'string') {
    try {
      const parsed = JSON.parse(responsePayload);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  if (typeof responsePayload === 'object') {
    return responsePayload as ProviderPhoneError;
  }

  return null;
};

const mapProviderPhoneErrorToMagma = (
  error: AxiosError
): MappedErrorType | null => {
  const data = error.response?.data as any;
  const providerError = parseProviderPhoneError(data);
  if (!providerError) return null;

  const reason =
    typeof providerError.reason === 'string' ? providerError.reason : '';
  const url = error.config?.url || '';

  const isAmbassadorPhone = url.includes('/sign-up-ambassador/phone');
  const isProspectSendCode = url.includes('/sign-up-prospect/phone/send-code');
  const isReferredSendCode = url.includes('/sign-up-prospect/referred/phone/send-code');

  if (!isAmbassadorPhone && !isProspectSendCode && !isReferredSendCode) {
    return null;
  }

  const typeKey = isAmbassadorPhone
    ? 'ERROR-REGISTER-PHONE-SIGN-UP-HELPER'
    : isReferredSendCode
      ? 'ERROR-SEND-CODE-REFERRED'
      : 'ERROR-SEND-CODE';

  const subtypeMap: Record<string, string> = {
    'invalid_phone_line': 'ERROR-INVALID-PHONE-NUMBER',
    'blocked_country': 'ERROR-BLOCKED-COUNTRY',
    'non_sms_line_type': 'ERROR-NON-SMS-LINE-TYPE',
  };

  return {
    type: typeKey,
    subtype: subtypeMap[reason] || 'ERROR-WHILE-SENDING-CODE',
  };

};

// Request interceptors (attach Authorization when token is present)
const attachAuthHeader =
  (persona: 'user' | 'prospect' | 'admin' | 'any' = 'any') =>
  (config: any) => {
    const jwtToken = readJwtToken(
      persona === 'any' ? undefined : persona,
      true
    );
    if (jwtToken && config.url !== '/signin') {
      if (!config.headers) {
        config.headers = {} as AxiosRequestHeaders;
      }
      config.headers.Authorization = `Bearer ${jwtToken}`;
    }

    if (!config.headers) {
      config.headers = {} as AxiosRequestHeaders;
    }
    config.headers['X-Device-Id'] = getDeviceId();
    const integrationUuid = new URLSearchParams(window.location.search).get('integrationUuid');
    if (integrationUuid && /sign-up-prospect|sign-up-prospect-form/.test(config.url || '')) {
      config.params = { ...config.params, integrationUuid };
    }
    return config;
  };

instances.main.interceptors.request.use(attachAuthHeader());
instances.userDashboard.interceptors.request.use(attachAuthHeader('user'));
instances.prospectDashboard.interceptors.request.use(
  attachAuthHeader('prospect')
);
instances.reviews.interceptors.request.use(attachAuthHeader());
instances.helperSignUp.interceptors.request.use(attachAuthHeader('user'));

// Response interceptors
// When the backend bans an IP it returns 403 with subtype 'IP-BANNED'. Send the
// user to the dedicated /blocked page so they understand they're blocked and how
// to reach us. Handled explicitly here (rather than via magma-errors' redirect)
// so it works regardless of the deployed magma-errors version.
const _redirectIfIpBanned = (error: AxiosError): boolean => {
  const data = error.response?.data as any;
  if (data?.subtype !== 'IP-BANNED') return false;
  const ip = data.ip ? String(data.ip) : '';
  try {
    if (ip) sessionStorage.setItem('magma_blocked_ip', ip);
  } catch {
    // ignore storage errors
  }
  // Guard against reload loops if a request fails while already on /blocked.
  if (window.location.pathname !== '/blocked') {
    hardRedirect(ip ? `/blocked?ip=${encodeURIComponent(ip)}` : '/blocked');
  }
  return true;
};

const handleStandardError = (error: AxiosError) => {
  if (redirectUnavailableAccess(error)) return Promise.reject(error);
  if ((error.response?.data as any)?.disabled === true) {
    const language = (error.response?.data as any)?.language === 'en' ? 'en' : 'fr';
    if (window.location.pathname !== '/program-ended') {
      void import('@/router').then(({ default: router }) => router.replace(`/program-ended?lang=${language}`));
    }
    return Promise.reject(error);
  }
  if (_redirectIfIpBanned(error) || recoverInvalidSession(error)) return Promise.reject(error);

  const data = error.response?.data as any;
  const status = error.response?.status;
  const url = error.config?.url || '';
  let errorType = data?.type as string;
  let errorSubType = data?.subtype as string;

  if (!errorType || !errorSubType) {
    const mappedError = mapProviderPhoneErrorToMagma(error);
    if (mappedError) {
      if (data && typeof data === 'object') {
        data.type = mappedError.type;
        data.subtype = mappedError.subtype;
        errorType = data.type;
        errorSubType = data.subtype;
      } else if (error.response) {
        error.response.data = { ...mappedError };
        errorType = mappedError.type;
        errorSubType = mappedError.subtype;
      }
    }
  }

  // SMS rate-limit middleware currently returns plain 429 without type/subtype.
  // Normalize it to magma-errors generic key so all user-facing copy stays centralized there.
  if ((!errorType || !errorSubType) && status === 429 && _isPhoneFlowRoute(url)) {
    if (data && typeof data === 'object') {
      data.type = 'GENERIC';
      data.subtype = 'TOO-MANY-REQUESTS';
      errorType = data.type;
      errorSubType = data.subtype;
    } else if (error.response) {
      error.response.data = {
        type: 'GENERIC',
        subtype: 'TOO-MANY-REQUESTS',
      };
      errorType = 'GENERIC';
      errorSubType = 'TOO-MANY-REQUESTS';
    }
  }

  const retryableSubtypes = [
    'ERROR-WHILE-SENDING-CODE',
    'ERROR-PRELUDE-RATE-LIMIT',
    'ERROR-PRELUDE-TIMEOUT',
    'ERROR-SERVICE-TEMPORARILY-UNAVAILABLE',
    'TOO-MANY-REQUESTS',
    'ERROR-SMS-BUDGET-EXCEEDED',
    'ERROR-MAX-ATTEMPTS-REACHED',
    'CHECK-ERROR',
  ];
  if (_isPhoneFlowRoute(url) && (status === 429 || errorSubType === 'ERROR-PRELUDE-RATE-LIMIT')) {
    applyPhoneRateLimit(error.response?.headers?.['retry-after']);
  }
  const nonRetryableSubtypes = [
    'ERROR-BLOCKED-COUNTRY',
    'ERROR-NON-SMS-LINE-TYPE',
    'ERROR-INVALID-PHONE-NUMBER',
    'ERROR-PHONE-NOT-VALID',
    'ERROR-TEMPORARY-PHONE-NUMBER',
  ];

  if (data && typeof data === 'object') {
    data.retryable = status === 429 || retryableSubtypes.includes(errorSubType);
    data.shouldChangeNumber = nonRetryableSubtypes.includes(errorSubType);
  }

  // Not a failure for the prospect: the sign-up flows catch it and skip the code step
  if (errorSubType === 'PHONE-NUMBER-ALREADY-VERIFIED' && url.includes('/sign-up-prospect/phone/send-code')) {
    return Promise.reject(error);
  }

  const err = findError(errorType as ErrorKeysType, errorSubType);

  if (!err) return Promise.reject(error);

  if (err.actionType === 'custom') return error;

  if (err.message) {
    const commonStore = useCommonStore();
    commonStore.setAlert({
      open: true,
      type: 'error',
      title: i18n.global.t('errors.title'),
      description: localizeErrorMessage(err.message) || i18n.global.t('generic.error'),
    });
  }

  if (err.actionType === 'redirect') {
    setTimeout(() => {
      hardRedirect(err.redirectPath as string);
    }, 3000);
  }

  return Promise.reject(error);
};

instances.main.interceptors.response.use(
  (response) => response,
  (error) => handleStandardError(error)
);

instances.userDashboard.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // A banned IP must land on /blocked, not be bounced to /login below.
    if (redirectUnavailableAccess(error) || _redirectIfIpBanned(error) || recoverInvalidSession(error)) return Promise.reject(error);

    // Special cases to avoid hanging loaders
    const url = error?.config?.url || '';
    if (
      url.includes('/auth/jwt-token') &&
      [400, 401, 403, 404].includes(error.response?.status)
    ) {
      const data = error.response?.data as any;
      // The link is valid but the account has no access anymore (removed from every teamspace,
      // or archived): a new login link would fail the same way, so explain it instead
      if (['USER-NOT-AMBASSADOR', 'USER-ARCHIVED'].includes(data?.subtype)) {
        hardRedirect('/no-access');
        return Promise.reject(error);
      }

      // Show a toast then redirect to login
      const err = findError(data?.type as ErrorKeysType, data?.subtype as any);
      const commonStore = useCommonStore();
      commonStore.setAlert({
        open: true,
        type: 'error',
        title: i18n.global.t('errors.title'),
        description:
          localizeErrorMessage(err?.message) ||
          i18n.global.t('errors.sessionExpired'),
      });
      // Session expired mid-navigation: come back here after logging in.
      saveReturnTo(window.location.pathname + window.location.search);
      setTimeout(() => hardRedirect('/login'), 1500);
      return Promise.reject(error);
    }
    return handleStandardError(error);
  }
);

instances.reviews.interceptors.response.use(
  (response) => response,
  (error) => handleStandardError(error)
);

instances.prospectDashboard.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error?.config?.url || '';
    if (redirectUnavailableAccess(error) || _redirectIfIpBanned(error) || recoverInvalidSession(error)) return Promise.reject(error);
    if (url.includes('/auth/jwt-token') && [400, 401, 403, 404].includes(error.response?.status)) {
      const data = error.response?.data as any;
      const err = findError(data?.type as ErrorKeysType, data?.subtype as any);
      const commonStore = useCommonStore();
      commonStore.setAlert({
        open: true,
        type: 'error',
        title: i18n.global.t('errors.title'),
        description:
          localizeErrorMessage(err?.message) ||
          i18n.global.t('errors.sessionExpired'),
      });
      // Session expired mid-navigation: come back here after logging in.
      saveReturnTo(window.location.pathname + window.location.search);
      setTimeout(() => hardRedirect('/login'), 1500);
      return Promise.reject(error);
    }
    return handleStandardError(error);
  }
);
